<template>
  <div>
    <div v-if="!validToken">
      <h1 class="text-center" v-html="invalidUrlMessage || 'INVALID URL'"/>
    </div>
    <div v-else v-loading="loading" class="h-full text-center">
      <div class="flex justify-center">
        <el-image v-if="data.tenant.logo" :src='data.tenant.logo' fit='cover' class="h-20 float-left" lazy @error="handleLogoImageError">
            <template #error>
              <span class="text-gray-600 text-base font-bold">{{ data.tenant.companyName }}</span>
            </template>
        </el-image>
        <strong v-else>{{ data.tenant.companyName  }}</strong>
      </div>

      <h1>Vehicle Photo Log request for <i class="uil uil-truck text-2xl"></i> {{ data.vehicle.name }}</h1>
      <h2>Assigned to: {{ associateFullName }}</h2>
      <h2>For: {{ rosterDate }}</h2>

      <hr class="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700">

      <template v-if="validRosterDay">
        <VehicleDailyLogSpecificPhotos
          ref="vehicle-daily-log"
          :documents="data.documents"
          :requiredPhotos="requiredPhotos"
          :typeCapture="typeCapture"
          :allRequiredPhotosUploaded="allRequiredPhotosUploaded"
          :optionalPhotos="optionalPhotos"
          :specificPhotos="specificPhotos"
          :isValidRequiredPhotos="isValidRequiredPhotos"
          @handleAddNewSpecificPhotos="handleAddNewSpecificPhotos"
          @handleAddNewImage="handleAddNewImage"
          @handleOptionalImages="handleOptionalImages"
        >
        
          <template #title="{requiredPhotos}">
            <h3>
              Take current photos of <i class="uil uil-truck text-2xl"></i>
              {{ data.vehicle.name }}
            </h3>
            <p class="text-base pt-2 pb-5">
              Tap the {{ requiredPhotos ? "buttons" : "button" }} below to add
              photos...
            </p>
          </template>

          <template v-for="slotKey in ['specific', 'optional', 'documents']" v-slot:[slotKey]="{ image, index }">
            <div :key="slotKey" >
              <el-image
                :id="'img' + index"
                :src="image.url"
                class="h-36 w-full object-cover rounded-lg"
              >
                <div slot="placeholder" class="image-slot">
                  <div class="h-36 w-full flex items-center justify-center bg-gray-200 rounded-lg">
                    <i class="uil uil-image text-gray-400 text-4xl"></i>
                  </div>
                </div>

                <div slot="error" class="image-slot">
                  <div class="h-36 min-w-[160px] w-full flex items-center justify-center bg-gray-200 rounded-lg">
                    <i class="uil uil-image text-gray-400 text-4xl"></i>
                  </div>
                </div>
              </el-image>
              <div v-if="image.uploaded" class="absolute top-3 right-3">
                <div class="bg-green-500 rounded-full p-1">
                  <i class="w-6 el-icon-check text-white text-xl"></i>
                </div>
              </div>
              <div
                v-if="image.uploaded === false"
                class="absolute top-3 right-3"
              >
                <div class="bg-orange-500 rounded-full p-1">
                  <i class="w-6 el-icon-loading text-white text-xl"></i>
                </div>
              </div>
              <div v-if="image.error" class="absolute top-3 right-3">
                <div class="bg-red-500 rounded-full p-1">
                  <i class="w-6 el-icon-close text-white text-xl"></i>
                </div>
              </div>
            </div>
          </template>

        </VehicleDailyLogSpecificPhotos>
      </template>
      <template v-else>
        <p class="text-base">
          This Vehicle Photo Log is no longer accessible because it is no longer {{ rosterDate }}. If you're trying to upload photos for today, please ask your manager for a new link. Photos for previous days will need to be sent another way.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { previewHeicImage, isHEICFile} from '@/utilities/heicUtility.js'
import { convertAndCompressImage } from '@/utilities/imageUtility.js'
import { isAllowedPhotoFile, getPhotoTypeRejectionMessage } from '@/utilities/photoFileTypes.js'
import { API } from 'aws-amplify';
import axios from 'axios';
import VehicleDailyLogSpecificPhotos from './VehicleDailyLogSpecificPhotos.vue'
import { shutdown } from '@intercom/messenger-js-sdk';

// Associate-facing copy for a link that cannot be opened, keyed by the error type the
// public API returns. Single source of truth for these strings — mirrors the
// `errorTypeMessage` map in src/views/Settings/RosterChecklist/RosterChecklistPublic.vue.
// TOKEN_EXPIRED wording matches the Roster Checklist / backend expiry wording.
// INVALID_TOKEN deliberately does NOT claim the link expired: the same response covers a
// malformed/unknown URL as well as an expired link whose row was removed by DynamoDB TTL.
const INVALID_URL_MESSAGES = {
  TOKEN_EXPIRED: 'This link has expired. Please contact your Dispatch team for a new link.',
  INVALID_TOKEN: 'This link is no longer valid. Please contact your Dispatch team for a new link.',
};

// Generic friendly fallback, so an unrecognised type never renders a raw enum or an empty heading.
const DEFAULT_INVALID_URL_MESSAGE = INVALID_URL_MESSAGES.INVALID_TOKEN;

export async function markLinkAsOpened(token) {
  try {
    const apiName = 'heraPublicApi';
    const path = '/mark-link-opened/' + token;
    const result = await API.get(apiName, path);
  } catch (error) {
    if (error != "Error: No current user") {
      console.error(error);
    }
  }
}

export default {
  components:{
    VehicleDailyLogSpecificPhotos
  },
  data() {
    return {
      loading: false,
      invalidUrlMessage: null,
      validToken: true,
      validRosterDay: true,
      data: {
        tenant: { group: '', logo: '', companyName: '' },
        vehicle: { id: '', name: '' },
        associate: { firstName: '', lastName: '', id: ''},
        rosteredDay: { notesDate: '' }, // YYYY-MM-DD
        documents: [], // { url: '', type: '' }
      },
      requiredPhotos: false,
      specificPhotos: [],
      isValidRequiredPhotos: true,
      typeCapture: '',
      optionalPhotos: [],
      objectURLs: [],
      currentButtonNotification: null,
      viewName: "vehicle-daily-log-public",
      hasActiveUploads: false,
    }
  },
  
  beforeMount(){
    shutdown();
  },

  computed: {
    routeToken() {
      return this.$route.params.token;
    },

    associateFullName() {
      const {firstName, lastName} = this.data.associate;

      if (firstName && lastName) return `${firstName} ${lastName}`;
      if (firstName) return firstName;
      if (lastName) return lastName;

      return '—';
    },

    rosterDate() {
      const date = this.data.rosteredDay.notesDate;
      return this.$moment(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
    },

    allRequiredPhotosUploaded() {
      return this.specificPhotos.every(photoOption => {
        return photoOption.images.length > 0
      })
    }
  },

  methods:{
    async fetchDailyLogData() {
      try {

        this.loading = true;
        const apiName = 'heraPublicApi';
        const apiPath = `/u/vehicle-photo-logs/${this.routeToken}`;
        const response = await API.get(apiName, apiPath);
        this.data = response.body;
        const { tenant, specificPhotos=[], documents=[] } = this.data
       
        const supportedHeicTypes = new Set(['image/heic', 'image/heif'])
        const processedDocuments  = documents.map(async document => {
          if(supportedHeicTypes.has(document.type)){
            const response = await fetch(document.url)
            const heicBlob = await response.blob()
       
            const convertedBlob = await previewHeicImage(heicBlob)
            const convertedUrl = URL.createObjectURL(convertedBlob)
            this.objectURLs.push(convertedUrl)
            document.url = convertedUrl
          }
          return document
        })
        await Promise.all(processedDocuments)
        
        this.requiredPhotos = specificPhotos.length > 0;
        this.typeCapture = (!tenant.allowLibraryUpload) ? 'camera' : null
        if (specificPhotos.length > 0) {
          this.specificPhotos = this.mapOptionsCustomList(specificPhotos);
          this.renderSavedDocuments(this.data.documents);
        }
        this.isValidRequiredPhotos = this.validateRequiredPhotos()
      }
      catch (error) {
        if (!error?.response?.data) {
          this.displayUserError(error.message);
          return;
        }

        this.handleErrorData(error.response.data);
      }
      finally {
        this.loading = false;
      }
    },

    displayUserError(message) {
      this.$notify({
        title: 'Error',
        type: "error",
        message: message,
        duration: 2500,
        customClass:'uil uil-exclamation-triangle'
      });
    },

    // Resolves the associate-facing copy for an unusable link from the error type.
    resolveInvalidUrlMessage(type) {
      return INVALID_URL_MESSAGES[type] || DEFAULT_INVALID_URL_MESSAGE;
    },

    handleErrorData(data) {
      const { payload, type, message } = data;
      if (payload) {
        this.data = {...this.data, ...payload};
      }

      if (type === 'INVALID_TOKEN' || type === 'TOKEN_EXPIRED') {
        this.validToken = false;
        // A server-provided message always wins: it carries the friendly expiry copy and the
        // "no longer assigned to Vehicle X" wording, both of which are HTML rendered via v-html.
        this.invalidUrlMessage = message || this.resolveInvalidUrlMessage(type);
        return;
      }
      if (type === 'INVALID_ROSTER_DAY') {
        this.validRosterDay = false;
        return;
      }
    },

    async handleLogoImageError() {
      if(this.data.tenant.key && /\.heic$/i.test(this.data.tenant.key)){
        try {
          const response = await fetch(this.data.tenant.logo)
          if (!response.ok) throw new Error("Error downloading image")
          const heicBlob = await response.blob()
          const convertedBlob = await previewHeicImage(heicBlob)
          const convertedUrl = URL.createObjectURL(convertedBlob)
          this.data.tenant.logo = convertedUrl
          this.objectURLs.push(convertedUrl)
        } catch (error) {
          this.data.tenant.logo = ''
        }
      }else{
        this.data.tenant.logo = ''
      }
    },

    async handleAddNewImage(e) {
      const files = e.target.files || e.dataTransfer.files;

      const rejectedFileNames = [];
      let filesToUpload = [];

      try {
        for (const file of files) {
          let fileType = file.type
          if(!fileType && /\.heic$/i.test(file.name)){
            fileType = 'image/heic'
          }

          const compressedFile = await convertAndCompressImage(file)
          const url = URL.createObjectURL(compressedFile)
          this.objectURLs.push(url)

          if(!isAllowedPhotoFile(file)) {
            rejectedFileNames.push(file.name);
            continue;
          }

          const fileObject = {
            file: compressedFile || file,
            fileName: compressedFile?.name || file.name,
            fileType: compressedFile?.type || fileType,
            url,
            uploaded: false
          }
          const imagesArray = this.data.documents
          const indexImage = imagesArray.length
          this.data.documents.push({...fileObject, index: indexImage})

          this.updateUploadStatus();

          filesToUpload.push({...fileObject, index: indexImage});
        }

        if (rejectedFileNames.length) {
          this.displayUserError(getPhotoTypeRejectionMessage(rejectedFileNames));
        }

        if(filesToUpload.length > 0){
          await this.uploadPhotos(filesToUpload, this.handleFileUpload);
        }
      }
      catch (error) {
        console.error(error);
        this.displayUserError(error.message);
      }
      finally {
        this.$refs['vehicle-daily-log'].$refs.imageUploadInput.value = '';
      }
    },

    async getSignedUrl(s3Key, fileType) {
      const apiName = 'heraPublicApi';
      const apiPath = `/u/vehicle-photo-logs/${this.routeToken}/get-upload-url`;
      const {uploadUrl} = await API.post(apiName, apiPath, {
        body: { s3Key, contentType: fileType },
      });
      return uploadUrl;
    },

    async createDocumentRecord(s3Key, file, optionId, fileType) {
      const dateToString = (new Date()).toISOString();
      const documentDate = this.removeTZfromDate(dateToString);

      const documentPayload = {
        group: this.data.tenant.group,
        name: file.name,
        type: fileType,
        key: s3Key,
        documentDate,
        uploadDate: (new Date()).toISOString(),
        takenByAssociateId: this.data.associate.id,
        ...(optionId && { documentOptionCustomListsId: optionId })
      };
      const apiName = 'heraPublicApi';
      const apiPath = `/u/vehicle-photo-logs/${this.routeToken}`;
      const options = { body: documentPayload };
      const newDocument = await API.post(apiName, apiPath, options);
      return newDocument;
    },

    getPlainDate(date=new Date(), format) {
      if (typeof date === 'string') return date.replace(/\//ig, '-');
      if (!(date instanceof Date)) return date;
      const dateFormat = format || 'YYYY-MM-DD';
      return this.$moment(date).format(dateFormat);
    },

    async handleFileUpload(fileToSave, indexImage) {
      let { file, fileType } = fileToSave;
      const millisNow = (new Date()).getTime();
      const plainDate = this.getPlainDate(); // YYY-MM-DD

      const s3Key = 'photo-log-images/' +
        this.data.tenant.group + '/vehicles/' +
        this.data.vehicle.id.toLowerCase() +
        `/${plainDate}/${millisNow}-${file.name}`;

      // Upload file to S3
      try {
        const newDocument = await this.tryUploadingPhotos(file, fileType, s3Key);
        if(newDocument.type === "image/heic" || /\.heic$/i.test(newDocument.name)){
          const response = await fetch(newDocument.url)
          const heicBlob = await response.blob()
        
          const convertedBlob = await previewHeicImage(heicBlob)
          const convertedUrl = URL.createObjectURL(convertedBlob)
          newDocument.url = convertedUrl
          this.objectURLs.push(convertedUrl)
          this.$set(this.data.documents[indexImage], "url", convertedUrl);
        }
        this.$set(this.data.documents[indexImage], "uploaded", true);
      } catch (error) {
        const imagesToUpdate = this.data.documents;
        imagesToUpdate[indexImage].error = true;
        imagesToUpdate[indexImage].uploaded = null;
        this.displayUserNotificationWithButton({
            title: 'Update Vehicle Photo Log',
            type: "warning",
            duration: 0,
            message: error,
        }, this.viewName, () => {
          this.data.documents = [];
          this.fetchDailyLogData();
        }, false, "Click here", this.hasActiveUploads);
      } finally {
        this.updateUploadStatus();
      }
    },

    async handleFilesUpload(fileToSave, indexImage) {
      let { file, optionId, fileType } = fileToSave;
      
      const millisNow = (new Date()).getTime();
      const plainDate = this.getPlainDate(); // YYY-MM-DD
      
      const s3Key = 'photo-log-images/' +
        this.data.tenant.group + '/vehicles/' +
        this.data.vehicle.id.toLowerCase() +
        `/${plainDate}/${millisNow}-${file.name}`;

      // Upload file to S3
      try {
        const newDocument = await this.tryUploadingPhotos(file, fileType, s3Key, optionId);
        if(newDocument.type === "image/heic" || /\.heic$/i.test(newDocument.name)){
          const response = await fetch(newDocument.url)
          const heicBlob = await response.blob()
        
          const convertedBlob = await previewHeicImage(heicBlob)
          const convertedUrl = URL.createObjectURL(convertedBlob)
          newDocument.url = convertedUrl
          this.objectURLs.push(convertedUrl)
          if (optionId) {
            let indexStoreToImages = this.specificPhotos.findIndex(item => item.id == optionId);
            const imagesToUpdate = this.specificPhotos[indexStoreToImages].images;
            this.$set(imagesToUpdate[indexImage], "url", convertedUrl);
          } else {
            this.$set(this.optionalPhotos[indexImage], "url", convertedUrl);
          }
        }
        if (optionId) {
          let indexStoreToImages = this.specificPhotos.findIndex(item => item.id == optionId);
          const imagesToUpdate = this.specificPhotos[indexStoreToImages].images;
          this.$set(imagesToUpdate[indexImage], "uploaded", true);
        } else {
          this.$set(this.optionalPhotos[indexImage], "uploaded", true);
        }
      } catch (error) {
        if (optionId) {
          let indexStoreToImages = this.specificPhotos.findIndex(item => item.id == optionId);
          const imagesToUpdate = this.specificPhotos[indexStoreToImages].images;
          imagesToUpdate[indexImage].error = true;
          imagesToUpdate[indexImage].uploaded = null;
        } else {
          this.optionalPhotos[indexImage].error = true;
          this.optionalPhotos[indexImage].uploaded = null
        }
        this.displayUserNotificationWithButton({
            title: 'Update Vehicle Photo Log',
            type: "warning",
            duration: 0,
            message: error,
        }, this.viewName, () => {
          this.optionalPhotos = [];
          this.specificPhotos = [];
          this.fetchDailyLogData();          
        }, false, "Click here", this.hasActiveUploads);
      } finally {
        this.updateUploadStatus();
      }
    },

    async tryUploadingPhotos(file, fileType, s3Key, optionId=null){

      let newDocument = null;
      let attempts = 0;
      const maxAttempts = 2;
      while (attempts < maxAttempts) {
        try{
          const signedUrl = await this.getSignedUrl(s3Key, fileType);
          //Try uploading the file to S3
          await axios.put(signedUrl, file, {
            headers: { 'Content-Type': fileType },
          });
          newDocument = await this.createDocumentRecord(s3Key, file, optionId, fileType);
          break;
        }catch(error){
          attempts++;
          console.error(`Attempt ${attempts} failed:`, error);
          if (attempts >= maxAttempts) {
            throw 'There was an error uploading your photos. Please refresh this page and try again.';
          }
        }
      }
      return newDocument
    },

    async handleOptionalImages(event) {
      try {
        const files = event.target.files || event.dataTransfer.files;

        const rejectedFileNames = [];
        let filesToUpload = [];
        for (const file of files) {
          let fileType = file.type
          if(!fileType && /\.heic$/i.test(file.name)){
            fileType = 'image/heic'
          }
          const compressedFile = await convertAndCompressImage(file)
          const url = URL.createObjectURL(compressedFile)
          this.objectURLs.push(url)
      
          if(!isAllowedPhotoFile(file)) {
            rejectedFileNames.push(file.name)
            continue
          }

          const fileObject = {
            file: compressedFile || file,
            fileName: compressedFile?.name || file.name,
            fileType: compressedFile?.type || fileType,
            url,
            uploaded: false
          }
          const imagesArray = this.optionalPhotos
          const indexImage = imagesArray.length
          this.optionalPhotos.push({...fileObject, index: indexImage})

          this.updateUploadStatus();

          filesToUpload.push({...fileObject, index: indexImage})
        }

        if(rejectedFileNames.length) {
          this.displayUserError(getPhotoTypeRejectionMessage(rejectedFileNames))
        }

        if(filesToUpload.length > 0){
          await this.uploadPhotos(filesToUpload, this.handleFilesUpload);
        }
        
      } catch (error) {
        this.displayUserError(error);
      } finally {
        this.$refs['vehicle-daily-log'].$refs['imageOptionalUploadInput'].value = '';
      }
    },


    async handleAddNewSpecificPhotos(event, id) {

      try {
        const indexStoreToImages = this.specificPhotos.findIndex(item => item.id == id);
        const files = event.target.files || event.dataTransfer.files;

        const rejectedFileNames = []
        let filesToUpload = [];

        for (const file of files) {
          let fileType = file.type
          if(!fileType && /\.heic$/i.test(file.name)){
            fileType = 'image/heic'
          }

          const compressedFile = await convertAndCompressImage(file)
          const url = URL.createObjectURL(compressedFile)
          this.objectURLs.push(url)
      
          if (!isAllowedPhotoFile(file)) {
            rejectedFileNames.push(file.name);
            continue
          }

          const fileObject = {
            file: compressedFile || file,
            fileName: compressedFile?.name || file.name,
            fileType: compressedFile?.type || fileType,
            url,
            optionId: id,
            uploaded: false
          }
          const imagesArray = this.specificPhotos[indexStoreToImages].images
          const indexImage = imagesArray.length
          imagesArray.push({ ...fileObject, index: indexImage})

          this.updateUploadStatus();

          filesToUpload.push({...fileObject, index: indexImage})
        }

        if (rejectedFileNames.length) {
          this.displayUserError(getPhotoTypeRejectionMessage(rejectedFileNames))
        }

        if (filesToUpload.length > 0) {
          await this.uploadPhotos(filesToUpload, this.handleFilesUpload);
        }

      } catch (error) {
        this.displayUserError(error);
      }
      finally {
        this.$refs['vehicle-daily-log'].$refs[`imageUploadInput${id}`][0].value = '';
      }
    },

    havePendingDocumentsToUpload( arrayImages ) {
      return arrayImages.some(image => !image.uploaded)
    },

    validateRequiredPhotos() {
      return this.specificPhotos.every(item=> {
        return this.havePendingDocumentsToUpload(item.images)
      });
    },

    mapOptionsCustomList(options) {
      return options.map(option => {
        return {
          ...option,
          images: []
        }
      })
    },

    renderSavedDocuments(documents) {
      const requiredDocuments = documents.filter(document => document.optionId);
      const optionalDocuments = documents.filter(document => !document.optionId);
      requiredDocuments.forEach(document => {
        const indexStoreToImages = this.specificPhotos.findIndex(item => item.id == document.optionId);
        let fileObject = {
          id: document.id,
          url: document.url
        }
        this.specificPhotos[indexStoreToImages].images.push(fileObject);
      });

      optionalDocuments.forEach(document => {
        let fileObject = {
          id: document.id,
          url: document.url,
        }
        this.optionalPhotos.push(fileObject);
      })

    },

    async uploadPhotos(photos, callback) {
      await Promise.all(photos.map(photo => callback(photo, photo.index).catch(error => {
        console.error(`Error uploading photo ${photo.fileName}:`, error);
      })));
    },

    updateUploadStatus() {
      if (this.requiredPhotos) {
        this.hasActiveUploads = this.specificPhotos.some(photoOption => {
          return photoOption.images.some(image => image.uploaded === false)
        }) || this.optionalPhotos.some(image => image.uploaded === false);
      } else {
        this.hasActiveUploads = this.data.documents.some(image => image.uploaded === false);
      }
      if (!this.hasActiveUploads) {
        this.enableNotificationButton();
      }
    },

    enableNotificationButton() {
      const viewName = this.viewName;
      const buttonId = this.currentButtonNotification[`${viewName}-buttonId`];

      if (buttonId) {
        const buttonElement = document.getElementById(buttonId);
        if (buttonElement) {
          buttonElement.removeAttribute("disabled");
        }
      }
    },

    async markLinkAsOpened() {
      await markLinkAsOpened(this.routeToken);
    }
  },

  created() {
    if (!this.currentButtonNotification) {
      this.currentButtonNotification = {};
    }
    this.markLinkAsOpened();
    this.fetchDailyLogData();
  },

  beforeDestroy(){
    this.clearObjectURLs(this.objectURLs)
  }
}
</script>
